/* eslint-disable react-hooks/exhaustive-deps */
import "./FirstMarketplace.scss";
import { FunctionComponent, useContext, useEffect, useState } from "react";
import ToggleButton from "../../components/ToggleButton/ToggleButton";
import { useLazyQuery } from "@apollo/client";
import { useSearchParams } from "react-router-dom";
import {
  AccountConfig,
  Channel,
  Marketplace,
  MarketplaceConfig,
  SettingTypes,
} from "TypesAndInterfaces";
import { getMarketplaceQuery } from "pages/Marketplaces/queries";
import { displayError } from "utils/displayNotification";
import FirstMarketplaceForm from "components/FirstMarketplaceForm/FirstMarketplaceForm";
import FirstMarketplaceScheduler from "components/FirstMarketplaceScheduler/FirstMarketplaceScheduler";
import { initialState } from "utils/validation";
import FirstMarketplaceProvider, { FirstMarketplaceContext } from "components/FirstMarketplaceForm/FirstMarketplace.context";

const FirstMarketplace: FunctionComponent = () => {
  const [isProviderMounted, setIsProviderMounted] = useState(false);

  useEffect(() => {
    setIsProviderMounted(true);
  }, []);

  return (
    <FirstMarketplaceProvider>
      {isProviderMounted && <FirstMarketplaceContent />}
    </FirstMarketplaceProvider>
  );
};


const FirstMarketplaceContent: FunctionComponent = () => {
  //#region hooks
  const [disabledCreatedMarketplace, setDisabledCreatedMarketplace] =
    useState(true);

  const [searchParams] = useSearchParams();
  const [marketplaceConfig, setMarketplaceConfig] =
    useState<MarketplaceConfig>(initialState);
  const [shallowConfig, setShallowConfig] = useState<Partial<AccountConfig>>();
  const [marketplaceId, setMarketplaceId] = useState<number>();
  const [marketplaceName, setMarketplaceName] = useState("");
  const [isInvoicePullInitialized, setIsInvoicePullInitialized] =
    useState(false);
  const [firstInvoiceCycleDate, setFirstInvoiceCycleDate] = useState<Date>(new Date());
  const [emailList, setEmailList] = useState("");
  const [pageTitle, setPageTitle] = useState("");
  const [marketplaceChannels, setMarketplaceChannels] = useState<Channel[]>([]);

  const [isEditPayoutScheduleModalOpened, setIsEditPayoutScheduleModalOpened] =
    useState(false);

  const [editPayoutScheduleModalNumber, setEditPayoutScheduleModalNumber] =
    useState(0);

  const { setNewValues, } = useContext(FirstMarketplaceContext);

  //#endregion

  //#region API CALLS
  const [getMarketplace] = useLazyQuery<
    { getMarketplace: Marketplace },
    { id: number }
  >(getMarketplaceQuery, {
    fetchPolicy: "no-cache",
    onCompleted: (dataMarketplace) => {
      const { config, channels } = dataMarketplace.getMarketplace;
      if (config.account.unsuspendNewShops === null) {
        config.account.unsuspendNewShops = true;
      }
      if (config.account.noOfSellerQuestions === null) {
        config.account.noOfSellerQuestions = 5;
      }
      if (config.account.useCommision === null) {
        config.account.useCommision = true;
      }

      if (config.account.miraklSsoEnabled === null) {
        config.account.miraklSsoEnabled = false;
      }
      if (dataMarketplace.getMarketplace.isInvoicePullInitialized === null) {
        dataMarketplace.getMarketplace.isInvoicePullInitialized = false;
      }
      channels.forEach((channel) => {
        if (!channel.notificationSettings) {
          channel.notificationSettings = {
            configuration: { hmac: "", password: "", user: "" },
            transfer: { hmac: "", password: "", user: "" },
            transferInstrument: { hmac: "", password: "", user: "" },
            legalEntity: { hmac: "", password: "", user: "" },
          };
        }
      });
      setMarketplaceChannels(channels);
      setMarketplaceConfig(config);
      setShallowConfig({
        invoiceSyncScheduler: config.account.invoiceSyncScheduler,
        accountFullSyncScheduler: config.account.accountFullSyncScheduler,
        accountPartialSyncScheduler: config.account.accountPartialSyncScheduler,
      });
      setMarketplaceName(dataMarketplace.getMarketplace.name);
      setIsInvoicePullInitialized(
        dataMarketplace.getMarketplace.isInvoicePullInitialized
      );
      setFirstInvoiceCycleDate(
        dataMarketplace.getMarketplace.firstInvoiceCycleDate
      );
      setEmailList(joinEmails(config.account.marketplaceOperatorEmails));
      setMarketplaceId(dataMarketplace.getMarketplace.id);
      setDisabledCreatedMarketplace(dataMarketplace.getMarketplace.disabled);
      setPageTitle(`Update ${dataMarketplace.getMarketplace.name}`);
    },
    onError: (e) => {
      displayError(e.message);
    },
  });
  //#endregion

  useEffect(() => {
    const id = searchParams.get("marketplaceId");
    if (id && !isNaN(+id)) {
      const idNumber = +id;
      setMarketplaceId(idNumber);
      getMarketplace({ variables: { id: idNumber } });
    } else {
      setPageTitle("Create New Marketplace");
    }
  }, [searchParams]);

  const joinEmails = (list: string[]) => list.join(", ");

  const handleEditPayoutScheduleModalClose = () => {
    if (shallowConfig) {
      const {
        invoiceSyncScheduler,
        accountFullSyncScheduler,
        accountPartialSyncScheduler,
      } = shallowConfig;
      switch (editPayoutScheduleModalNumber) {
        case 1:
          setMarketplaceConfig((prev) => ({
            ...prev,
            account: {
              ...prev.account,
              invoiceSyncScheduler,
            },
          }));
          break;
        case 2:
          setMarketplaceConfig((prev) => ({
            ...prev,
            account: {
              ...prev.account,
              accountFullSyncScheduler,
            },
          }));
          break;
        case 3:
          setMarketplaceConfig((prev) => ({
            ...prev,
            account: {
              ...prev.account,
              accountPartialSyncScheduler,
            },
          }));
          break;
        default:
          break;
      }
    }
    setIsEditPayoutScheduleModalOpened(false);
  };

  const handleCronChange = (cronValue: string, cronResult: string) => {
    const schedulerMap: { [key: number]: string } = {
      1: 'invoiceSyncScheduler',
      2: 'accountFullSyncScheduler',
      3: 'accountPartialSyncScheduler'
    };
    
    const schedulerKey = schedulerMap[editPayoutScheduleModalNumber];
  
    if (!schedulerKey) {
      console.error(`Invalid editPayoutScheduleModalNumber: ${editPayoutScheduleModalNumber}`);
      return;
    }
  
    const newSchedulerValue = { description: cronResult, value: cronValue };
  
    // Update marketplaceConfig
    setMarketplaceConfig((prev) => ({
      ...prev,
      account: {
        ...prev.account,
        [schedulerKey]: newSchedulerValue,
      },
    }));
  
    // Update newValues
    setNewValues((prevNewValues) => {
      const updatedValues = [...prevNewValues];
      const index = updatedValues.findIndex(
        (value) => value.property === schedulerKey
      );
  
      const newValue = {
        property: schedulerKey,
        value: newSchedulerValue,
        type: SettingTypes.Account,
      };
  
      if (index !== -1) {
        updatedValues[index] = newValue;
      } else {
        updatedValues.push(newValue);
      }
  
      return updatedValues;
    });
  };

  return (
      <div className="content-wrapper">
        <main className="main-container-wrapper">
          <section className="main-container">
            <header className="main-container__header">
              <h2 className="main-container__title">{pageTitle}</h2>
              <div className="main-container__second-title-container">
                <p>Marketplace Status</p>
                <ToggleButton
                  onChange={() =>
                    setDisabledCreatedMarketplace(!disabledCreatedMarketplace)
                  }
                  checked={!disabledCreatedMarketplace}
                  checkedText="enabled"
                  uncheckedText="disabled"
                />
              </div>
            </header>
            <div className="main-content">
              {marketplaceId && marketplaceName && (
                <FirstMarketplaceScheduler
                  marketplaceConfig={marketplaceConfig}
                  setMarketplaceConfig={setMarketplaceConfig}
                  marketplaceId={marketplaceId}
                  setIsEditPayoutScheduleModalOpened={
                    setIsEditPayoutScheduleModalOpened
                  }
                  setEditPayoutScheduleModalNumber={
                    setEditPayoutScheduleModalNumber
                  }
                  isInvoicePullInitialized={isInvoicePullInitialized}
                  firstInvoiceCycleDate={firstInvoiceCycleDate}
                  editPayoutScheduleModalNumber={editPayoutScheduleModalNumber}
                  isEditPayoutScheduleModalOpened={isEditPayoutScheduleModalOpened}
                  handleCronChange={handleCronChange}
                  handleEditPayoutScheduleModalClose={handleEditPayoutScheduleModalClose}
                />
              )}
              <FirstMarketplaceForm
                marketplaceConfig={marketplaceConfig}
                setMarketplaceConfig={setMarketplaceConfig}
                marketplaceId={marketplaceId}
                disabledCreatedMarketplace={disabledCreatedMarketplace}
                setPageTitle={setPageTitle}
                setShallowConfig={setShallowConfig}
                marketplaceName={marketplaceName}
                setMarketplaceName={setMarketplaceName}
                emailList={emailList}
                setEmailList={setEmailList}
                channels={marketplaceChannels}
              />
            </div>
          </section>
        </main>
      </div>
  );
};

export default FirstMarketplace;
